import { useState, useEffect } from 'react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { formatPrice, calculatePricing, getDeliveryTimeframe } from '@/lib/utils';
import { Trash2, Plus, Minus, ShoppingBag, Truck, Calendar, ArrowLeft, FileText, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CartUpsell from '@/components/CartUpsell';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Cart() {
  const { cart, updateQuantity, removeFromCart, getTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showProformaModal, setShowProformaModal] = useState(false);
  const [proformaLoading, setProformaLoading] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState('mumbai'); // 'mumbai' or 'outstation'
  const [installationRequired, setInstallationRequired] = useState(false);
  const [proformaForm, setProformaForm] = useState({
    buyer_name: '',
    company_name: '',
    gst_number: '',
    address: '',
    phone: '',
    email: '',
    delivery_location: deliveryLocation
  });

  const subtotal = getTotal();
  
  // Calculate installation cost (5% of subtotal if required)
  const installationCost = installationRequired ? Math.round(subtotal * 0.05) : 0;
  
  // Adjust pricing based on installation
  const pricing = calculatePricing(subtotal + installationCost);
  
  // Shipping is free only for Mumbai & Navi Mumbai
  const isFreeShipping = deliveryLocation === 'mumbai';

  // Calculate delivery date
  const deliveryTimeframe = getDeliveryTimeframe(subtotal);
  const deliveryDays = parseInt(deliveryTimeframe);
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + deliveryDays);
  const formattedDeliveryDate = deliveryDate.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  // Sync proforma delivery_location when main cart delivery location changes
  useEffect(() => {
    setProformaForm(prev => ({ ...prev, delivery_location: deliveryLocation }));
  }, [deliveryLocation]);


  // Calculate product savings (MRP - Sale Price for all items)
  const productSavings = cart.reduce((total, item) => {
    const mrp = item.product.mrp || item.product.price;
    const salePrice = item.product.price;
    const savings = (mrp - salePrice) * item.quantity;
    return total + savings;
  }, 0);

  const handleCheckout = () => {
    // Guest checkout enabled - directly navigate to checkout
    navigate('/checkout');
  };

  const handleProformaFormChange = (e) => {
    const { name, value } = e.target;
    setProformaForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDownloadProforma = async (e) => {
    e.preventDefault();
    
    if (!proformaForm.buyer_name || !proformaForm.company_name || !proformaForm.address || !proformaForm.phone || !proformaForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setProformaLoading(true);
    
    try {
      const cartItems = cart.map(item => ({
        product_id: item.product.id,
        name: item.product.name,
        price: item.product.price,
        quantity: item.quantity,
        image_url: item.product.image_url,
        dimensions: item.product.dimensions
      }));

      const response = await axios.post(
        `${API_URL}/generate-proforma`,
        {
          ...proformaForm,
          delivery_location: proformaForm.delivery_location,
          installation_required: installationRequired,
          installation_cost: installationCost,
          cart_items: cartItems
        },
        {
          responseType: 'blob'
        }
      );

      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Mobile browsers don't handle programmatic downloads well
      // Open in new tab on mobile, direct download on desktop
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Open PDF in new tab - user can save/share from there
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
          // Fallback if popup blocked - navigate current page
          window.location.href = url;
        }
        toast.success('Proforma Invoice opened! Tap the share/save icon to download.');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `Proforma_Invoice_${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Proforma Invoice downloaded successfully!');
      }
      
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      setShowProformaModal(false);
    } catch (error) {
      console.error('Error generating proforma:', error);
      toast.error('Failed to generate proforma. Please try again.');
    } finally {
      setProformaLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen noise-texture">
        <PremiumHeader />
        <div className="container mx-auto px-4 sm:px-8 py-20 text-center">
          <ShoppingBag className="w-24 h-24 mx-auto mb-8 text-muted-foreground" />
          <h1 className="text-4xl font-black uppercase mb-4">YOUR CART IS EMPTY</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Start adding products to your cart
          </p>
          <Link to="/products">
            <Button className="bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider font-bold px-12 py-6 text-lg">
              BROWSE PRODUCTS
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-12">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 rounded-none uppercase tracking-wider font-bold hover:bg-accent/10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase mb-10">SHOPPING CART</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.product.id}
                data-testid={`cart-item-${item.product.id}`}
                className="bg-white border border-border rounded-none p-4 sm:p-6 hover:border-accent transition-colors"
              >
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  {/* Image - Hide for accessories */}
                  {item.product.category !== 'Accessory' && (
                    <div className="w-full sm:w-32 h-40 sm:h-32 flex-shrink-0 border border-border rounded-none overflow-hidden bg-secondary">
                      <img
                        src={item.product.image_url}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Details */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">
                        {item.product.subcategory}
                      </p>
                      <h3 className="text-lg font-bold uppercase">{item.product.name}</h3>
                      {item.product.category !== 'Accessory' && item.product.dimensions && (
                        <p className="mono text-xs text-muted-foreground">
                          {typeof item.product.dimensions === 'object' 
                            ? item.product.dimensions?.size || JSON.stringify(item.product.dimensions)
                            : item.product.dimensions}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      {/* Quantity Controls */}
                      <div className="flex items-center space-x-3">
                        <Button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          data-testid={`decrease-quantity-${item.product.id}`}
                          variant="outline"
                          size="icon"
                          className="rounded-none w-8 h-8"
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="mono font-bold w-8 text-center" data-testid={`quantity-${item.product.id}`}>
                          {item.quantity}
                        </span>
                        <Button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          data-testid={`increase-quantity-${item.product.id}`}
                          variant="outline"
                          size="icon"
                          className="rounded-none w-8 h-8"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>

                      {/* Price */}
                      <div className="text-left sm:text-right">
                        {item.product.mrp && item.product.mrp > item.product.price && (
                          <p className="mono text-xs text-muted-foreground line-through">
                            {formatPrice(item.product.mrp * item.quantity)}
                          </p>
                        )}
                        <p className="mono text-lg font-bold text-primary">
                          {formatPrice(item.product.price * item.quantity)}
                        </p>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <Button
                      onClick={() => removeFromCart(item.product.id)}
                      data-testid={`remove-${item.product.id}`}
                      variant="ghost"
                      size="sm"
                      className="rounded-none uppercase tracking-wider text-xs hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3 mr-2" />
                      REMOVE
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {/* Upsell Section */}
            <CartUpsell cartItems={cart.map(item => item.product)} />
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border rounded-none p-4 sm:p-6 lg:p-8 sticky top-24">
              <h2 className="text-2xl font-black uppercase mb-6">ORDER SUMMARY</h2>

              {/* Product List with Country of Origin */}
              <div className="space-y-3 mb-6 pb-4 border-b border-border">
                {cart.map((item) => (
                  <div key={item.product.id} className="flex justify-between items-start gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-bold uppercase leading-tight">{item.product.name}</p>
                      <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      {item.product.mrp && item.product.mrp > item.product.price && (
                        <p className="mono text-xs text-muted-foreground line-through">{formatPrice(item.product.mrp * item.quantity)}</p>
                      )}
                      <p className="mono text-sm font-bold whitespace-nowrap">{formatPrice(item.product.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Country of Origin - Single line */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
                <img 
                  src="https://flagcdn.com/w20/in.png" 
                  alt="India"
                  className="w-5 h-3 object-cover rounded-sm"
                />
                <span className="text-sm font-semibold text-gray-700">Country of Origin: India</span>
              </div>

              {/* Delivery Location Selection */}
              <div className="mb-4 pb-4 border-b border-border">
                <label className="text-sm font-bold uppercase mb-2 block">Delivery Location</label>
                <select
                  value={deliveryLocation}
                  onChange={(e) => setDeliveryLocation(e.target.value)}
                  className="w-full p-3 border-2 border-border rounded-none text-sm font-medium bg-white focus:border-primary focus:outline-none"
                >
                  <option value="mumbai">Within Mumbai & Navi Mumbai</option>
                  <option value="outstation">Outside Mumbai & Navi Mumbai</option>
                </select>
              </div>

              {/* Installation Option */}
              <div className="mb-4 pb-4 border-b border-border">
                <label className="text-sm font-bold uppercase mb-3 block">Installation Service</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border-2 border-border rounded-none cursor-pointer hover:border-primary transition-colors">
                    <input
                      type="radio"
                      name="installation"
                      checked={!installationRequired}
                      onChange={() => setInstallationRequired(false)}
                      className="w-4 h-4 accent-primary"
                    />
                    <div>
                      <span className="font-bold text-sm">Do it Yourself</span>
                      <p className="text-xs text-gray-500">Self-assembly with provided instructions</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border-2 border-border rounded-none cursor-pointer hover:border-primary transition-colors">
                    <input
                      type="radio"
                      name="installation"
                      checked={installationRequired}
                      onChange={() => setInstallationRequired(true)}
                      className="w-4 h-4 accent-primary"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm">Installation Required</span>
                        <span className="text-primary font-bold text-sm">+{formatPrice(Math.round(subtotal * 0.05))}</span>
                      </div>
                      <p className="text-xs text-gray-500">Professional installation by our team (5% of subtotal)</p>
                      <p className="text-xs text-orange-600 mt-1 italic">* Client to provide on-site accommodation for installation team in case of overnight work</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="space-y-3 mono text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-bold" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                </div>

                {installationRequired && (
                  <div className="flex justify-between text-primary">
                    <span>Installation (5%):</span>
                    <span className="font-bold">{formatPrice(installationCost)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span>CGST (9%):</span>
                  <span className="font-bold">{formatPrice(pricing.cgst)}</span>
                </div>

                <div className="flex justify-between">
                  <span>SGST (9%):</span>
                  <span className="font-bold">{formatPrice(pricing.sgst)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Shipping:</span>
                  {isFreeShipping ? (
                    <span className="font-bold text-green-600">FREE</span>
                  ) : (
                    <span className="font-bold text-orange-600 text-xs">To be paid by Client</span>
                  )}
                </div>

                <div className="border-t border-border pt-3 mt-3">
                  <div className="flex justify-between text-lg">
                    <span className="font-bold">TOTAL:</span>
                    <span className="font-black" data-testid="cart-total">{formatPrice(pricing.subtotal + pricing.cgst + pricing.sgst)}</span>
                  </div>
                  
                  {productSavings > 0 && (
                    <div className="flex justify-between mt-3 bg-green-100 p-3 border-2 border-green-500">
                      <span className="text-green-700 font-black text-sm">🎉 You Save:</span>
                      <span className="text-green-700 font-black text-lg">{formatPrice(productSavings)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Timeframe */}
              <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-none">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-black uppercase text-blue-600">Estimated Delivery</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-black text-blue-700 mono mb-1">
                    {deliveryTimeframe}
                  </p>
                  <div className="flex items-center justify-center gap-2 text-blue-600">
                    <Calendar className="w-4 h-4" />
                    <p className="text-sm font-semibold">
                      Expected by {formattedDeliveryDate}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-center text-blue-500 mt-2 italic">
                  * May be reduced if items are in stock
                </p>
              </div>

              <Button
                onClick={handleCheckout}
                data-testid="proceed-to-checkout"
                className="w-full mt-6 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider font-bold py-6"
              >
                PROCEED TO CHECKOUT
              </Button>

              {/* Download Proforma Invoice Button */}
              <Button
                onClick={() => setShowProformaModal(true)}
                variant="outline"
                className="w-full mt-3 border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-none uppercase tracking-wide sm:tracking-wider font-bold py-4 sm:py-5 text-xs sm:text-sm"
              >
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0" />
                <span className="truncate">Download Proforma</span>
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2 px-2 leading-relaxed">
                Need approval from Boss/Finance? Get a formal quote PDF instantly
              </p>

              {/* B2B Link */}
              <div className="mt-6 p-3 sm:p-4 bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                <p className="text-xs sm:text-sm text-center leading-relaxed">
                  <span className="font-bold">Planning bulk orders?</span><br />
                  <Link to="/bulk-inquiry" className="text-primary hover:underline font-bold">
                    Get Custom Quote →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Proforma Modal */}
      {showProformaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-black uppercase flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Download Proforma Invoice
              </h2>
              <button onClick={() => setShowProformaModal(false)} className="p-2 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleDownloadProforma} className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Enter your details to generate a GST-compliant Proforma Invoice for approval.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="buyer_name" className="text-xs uppercase tracking-wider font-bold">
                  Your Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="buyer_name"
                  name="buyer_name"
                  value={proformaForm.buyer_name}
                  onChange={handleProformaFormChange}
                  placeholder="John Doe"
                  className="rounded-none border-2"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-xs uppercase tracking-wider font-bold">
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={proformaForm.company_name}
                  onChange={handleProformaFormChange}
                  placeholder="Acme Pvt. Ltd."
                  className="rounded-none border-2"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gst_number" className="text-xs uppercase tracking-wider font-bold">
                  GST Number <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="gst_number"
                  name="gst_number"
                  value={proformaForm.gst_number}
                  onChange={handleProformaFormChange}
                  placeholder="27AABCU9603R1ZM"
                  className="rounded-none border-2"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-xs uppercase tracking-wider font-bold">
                  Billing Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={proformaForm.address}
                  onChange={handleProformaFormChange}
                  placeholder="123 Business Park, Mumbai"
                  className="rounded-none border-2"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wider font-bold">
                    Phone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={proformaForm.phone}
                    onChange={handleProformaFormChange}
                    placeholder="+91 98765 43210"
                    className="rounded-none border-2"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-bold">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={proformaForm.email}
                    onChange={handleProformaFormChange}
                    placeholder="john@company.com"
                    className="rounded-none border-2"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delivery_location" className="text-xs uppercase tracking-wider font-bold">
                  Delivery Location <span className="text-red-500">*</span>
                </Label>
                <select
                  id="delivery_location"
                  name="delivery_location"
                  value={proformaForm.delivery_location}
                  onChange={handleProformaFormChange}
                  className="w-full p-3 border-2 border-border rounded-none text-sm font-medium bg-white focus:border-primary focus:outline-none"
                  required
                >
                  <option value="mumbai">Within Mumbai & Navi Mumbai</option>
                  <option value="outstation">Outside Mumbai & Navi Mumbai</option>
                </select>
              </div>

              <div className="bg-gray-50 p-4 border border-gray-200 mt-4">
                <p className="text-sm font-bold mb-2">Cart Summary:</p>
                <p className="text-sm">{cart.length} item(s) | Subtotal: {formatPrice(subtotal)}</p>
                {installationRequired && (
                  <p className="text-sm text-primary">Installation: {formatPrice(installationCost)}</p>
                )}
                <p className="text-sm font-bold">Total: {formatPrice(pricing.total)}</p>
                <p className="text-xs text-green-600 font-bold mt-1">✓ 30% Discount Already Applied on MRP</p>
                {proformaForm.delivery_location === 'mumbai' ? (
                  <p className="text-xs text-green-600 font-bold">✓ Free Delivery within Mumbai & Navi Mumbai</p>
                ) : (
                  <p className="text-xs text-orange-600 font-bold">⚠ Shipping charges to be paid by client</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={proformaLoading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold py-5 mt-4"
              >
                {proformaLoading ? (
                  'Generating PDF...'
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Download Proforma Invoice
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                PDF will be generated from Ezeefit Modular Furniture Pvt. Ltd.
              </p>
            </form>
          </div>
        </div>
      )}
      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <PageFAQ pageKey="cart" />
        <RelatedPages pageKey="cart" className="mt-12" />
      </div>
    </div>
  );
}
